<script setup>
import { computed } from 'vue';
import LineChart from 'shared/components/charts/LineChart.vue';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: String, required: true },
  infoText: { type: String, default: '' },
  // Full utility class (e.g. 'i-lucide-message-circle'), not just the icon
  // name -- @egoist/tailwindcss-icons statically scans source files for the
  // literal class string. `i-lucide-${icon}` here would never be generated,
  // since the scanner never sees the interpolated result as source text.
  icon: { type: String, default: 'i-lucide-chart-bar' },
  category: {
    type: String,
    default: 'neutral',
    validator: value => ['volume', 'time', 'rate', 'neutral'].includes(value),
  },
  // Signed percentage vs previous period. null/undefined hides the trend row.
  trend: { type: Number, default: null },
  // 'up' = higher is better (volume/rate metrics), 'down' = lower is better
  // (time metrics — a smaller avg resolution time is an improvement).
  trendGoodDirection: {
    type: String,
    default: 'up',
    validator: value => ['up', 'down'].includes(value),
  },
  sparkline: { type: Array, default: () => [] },
});

const CATEGORY_STYLES = {
  volume: { chip: 'bg-n-teal-3 text-n-teal-11', line: '#00B5A9' },
  time: { chip: 'bg-n-blue-3 text-n-blue-11', line: '#3B82F6' },
  rate: { chip: 'bg-n-amber-3 text-n-amber-11', line: '#F59E0B' },
  neutral: { chip: 'bg-n-slate-3 text-n-slate-11', line: '#64748B' },
};

const style = computed(() => CATEGORY_STYLES[props.category]);

// 0 covers both "genuinely flat" and calculateTrend's own sentinel for "no
// previous-period data to compare against" (useReportMetrics.js) — hide the
// row for both, same as the sibling ChartStats.vue (`v-if="metric.trend"`).
const hasTrend = computed(() => !!props.trend);
const isGoodTrend = computed(() => {
  if (!hasTrend.value) return false;
  return props.trendGoodDirection === 'up' ? props.trend > 0 : props.trend < 0;
});
const trendColorClass = computed(() =>
  isGoodTrend.value ? 'border-n-teal-10 text-n-teal-10' : 'border-n-ruby-9 text-n-ruby-9'
);

const hasSparkline = computed(() => props.sparkline.length >= 2);
const sparklineCollection = computed(() => ({
  labels: props.sparkline.map((_, index) => index),
  datasets: [
    {
      type: 'line',
      data: props.sparkline,
      borderColor: style.value.line,
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.35,
      fill: false,
    },
  ],
}));
const sparklineOptions = {
  plugins: { tooltip: { enabled: false } },
  scales: {
    x: { display: false },
    y: { display: false },
  },
};
</script>

<template>
  <div
    class="p-4 rounded-xl border border-n-weak bg-n-solid-2 flex flex-col gap-3"
  >
    <div class="flex items-center gap-2">
      <span
        class="inline-flex items-center justify-center size-7 rounded-md shrink-0"
        :class="style.chip"
      >
        <span :class="[icon, 'size-4']" />
      </span>
      <span class="text-sm text-n-slate-11 flex items-center gap-1">
        {{ label }}
        <fluent-icon
          v-if="infoText"
          v-tooltip="infoText"
          size="14"
          icon="info"
          class="text-n-slate-10"
        />
      </span>
    </div>

    <div class="flex items-end justify-between">
      <span class="text-2xl font-semibold text-n-slate-12">{{ value }}</span>
      <div
        v-if="hasTrend"
        class="text-xs flex items-center mb-1"
        data-test-id="dashboardStatTrend"
      >
        <div
          v-if="trend < 0"
          class="h-0 w-0 border-x-4 border-x-transparent border-t-[8px] mr-1"
          :class="trendColorClass"
        />
        <div
          v-else
          class="h-0 w-0 border-x-4 border-x-transparent border-b-[8px] mr-1"
          :class="trendColorClass"
        />
        <span class="font-medium" :class="trendColorClass">{{ trend }}%</span>
      </div>
    </div>

    <div v-if="hasSparkline" class="h-8 -mx-1">
      <LineChart :collection="sparklineCollection" :chart-options="sparklineOptions" />
    </div>
  </div>
</template>
